// File: frontend/src/components/STIX/BundleStatsCards.tsx
import React from 'react';
import { Paper, Typography, Box, CircularProgress, alpha, styled, useTheme } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { STIXStatistics } from '../../types/stix';
import { cyber } from '../../theme';

interface BundleStatsCardsProps {
  statistics: STIXStatistics | null;
  loading: boolean;
  latestUpdate?: string;
}

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(cyber.primary, 0.2)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: alpha(cyber.primary, 0.6),
    boxShadow: cyber.glow,
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1.5),
}));

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

const getTimeAgo = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

export const BundleStatsCards: React.FC<BundleStatsCardsProps> = ({
  statistics,
  loading,
  latestUpdate
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
            <StatsCard>
              <Box display="flex" justifyContent="center" alignItems="center" height={120}>
                <CircularProgress size={40} />
              </Box>
            </StatsCard>
          </Box>
        ))}
      </Box>
    );
  }

  if (!statistics) {
    return null;
  }

  // Each card gets its own accent (purple stays first as the brand anchor)
  const stats = [
    {
      icon: <InventoryIcon sx={{ fontSize: 28, color: cyber.primaryLight }} />,
      label: 'Total Bundles',
      value: formatNumber(statistics.total_bundles),
      accent: cyber.primaryLight,
      bgColor: alpha(theme.palette.primary.main, 0.12),
      mono: true
    },
    {
      icon: <DataObjectIcon sx={{ fontSize: 28, color: cyber.accent.cyan }} />,
      label: 'Total Objects',
      value: formatNumber(statistics.total_objects),
      accent: cyber.accent.cyan,
      bgColor: alpha(cyber.accent.cyan, 0.12),
      mono: true
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 28, color: cyber.accent.green }} />,
      label: 'Avg Objects/Bundle',
      value: statistics.average_objects_per_bundle.toFixed(1),
      accent: cyber.accent.green,
      bgColor: alpha(cyber.accent.green, 0.12),
      mono: true
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 28, color: cyber.accent.coral }} />,
      label: 'Latest Update',
      value: getTimeAgo(latestUpdate),
      accent: cyber.accent.coral,
      bgColor: alpha(cyber.accent.coral, 0.12),
      mono: false
    }
  ];

  return (
    <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
      {stats.map((stat, index) => (
        <Box key={index} sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
          <StatsCard
            elevation={2}
            sx={{
              '&:hover': {
                borderColor: alpha(stat.accent, 0.5),
                boxShadow: `0 0 0 1px ${alpha(stat.accent, 0.35)}, 0 4px 24px ${alpha(stat.accent, 0.2)}`,
              }
            }}
          >
            <IconWrapper sx={{ background: stat.bgColor, border: `1px solid ${alpha(stat.accent, 0.4)}` }}>
              {stat.icon}
            </IconWrapper>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {stat.label}
            </Typography>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ color: stat.accent, fontFamily: stat.mono ? cyber.fontMono : undefined }}
            >
              {stat.value}
            </Typography>
          </StatsCard>
        </Box>
      ))}
    </Box>
  );
};

export default BundleStatsCards;
